import { useState, useEffect, useRef, useCallback } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { BookOpen, Wifi, WifiOff, ArrowUpDown, Layers } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtUsdt = (v) => {
  if (v == null || isNaN(v)) return "--";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(2)}K`;
  return `$${v.toFixed(2)}`;
};

export default function OrderbookPage() {
  const [orderbook, setOrderbook] = useState({ bids: [], asks: [] });
  const [trades, setTrades] = useState([]);
  const [ticker, setTicker] = useState({});
  const [symbol, setSymbol] = useState("NOMOXUSDT");
  const [connected, setConnected] = useState(false);
  const [depthSummary, setDepthSummary] = useState(null);
  const wsRef = useRef(null);
  const pollingRef = useRef(null);
  const depthPollRef = useRef(null);
  const wsFailedRef = useRef(false);

  const pollDepthSummary = useCallback(async () => {
    try {
      const r = await api.get(`/market/depth-summary?symbol=${symbol}&bands=2,10,20`);
      setDepthSummary(r.data);
    } catch (e) { console.error("depth summary error", e); }
  }, [symbol]);

  const pollRest = useCallback(async () => {
    try {
      const [obR, trR, tkR] = await Promise.all([
        api.get(`/market/orderbook?symbol=${symbol}&limit=20`),
        api.get(`/market/trades?symbol=${symbol}&limit=20`),
        api.get(`/market/ticker?symbol=${symbol}`),
      ]);
      setOrderbook(obR.data);
      if (Array.isArray(trR.data)) setTrades(trR.data.slice(0, 20));
      setTicker(tkR.data);
    } catch (e) { console.error("REST poll error", e); }
  }, [symbol]);

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    pollRest();
    pollingRef.current = setInterval(pollRest, 3000);
  }, [pollRest]);

  const connect = useCallback(() => {
    if (wsRef.current) wsRef.current.close();
    const wsUrl = BACKEND_URL.replace("https://", "wss://").replace("http://", "ws://");
    let ws;
    try { ws = new WebSocket(`${wsUrl}/api/ws/market/${symbol}`); } catch { wsFailedRef.current = true; startPolling(); return; }
    const timeout = setTimeout(() => { wsFailedRef.current = true; ws.close(); startPolling(); }, 5000);
    ws.onopen = () => { clearTimeout(timeout); setConnected(true); if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; } };
    ws.onclose = () => { clearTimeout(timeout); setConnected(false); startPolling(); };
    ws.onerror = () => { clearTimeout(timeout); setConnected(false); };
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        if (data.orderbook) setOrderbook(data.orderbook);
        if (data.trades) setTrades(data.trades.slice(0, 20));
        if (data.ticker) setTicker(data.ticker);
      } catch {}
    };
    wsRef.current = ws;
  }, [symbol, startPolling]);

  useEffect(() => {
    connect();
    pollDepthSummary();
    depthPollRef.current = setInterval(pollDepthSummary, 3000);
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (depthPollRef.current) clearInterval(depthPollRef.current);
    };
  }, [connect, pollDepthSummary]);

  const maxBidQty = Math.max(...(orderbook.bids || []).map((b) => parseFloat(b[1]) || 0), 1);
  const maxAskQty = Math.max(...(orderbook.asks || []).map((a) => parseFloat(a[1]) || 0), 1);

  return (
    <div className="space-y-6" data-testid="orderbook-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Live Orderbook</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Real-time market depth from MEXC</p>
        </div>
        <div className="flex items-center gap-3">
          <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())} className="w-40 bg-secondary/50 font-mono text-sm" data-testid="orderbook-symbol-input" />
          <Badge variant={connected ? "default" : "destructive"} className={connected ? "bg-emerald-500/15 text-emerald-500 border-transparent" : ""}>
            {connected ? <><Wifi className="w-3 h-3 mr-1" />Live</> : <><WifiOff className="w-3 h-3 mr-1" />Disconnected</>}
          </Badge>
        </div>
      </div>

      {/* Price Banner */}
      <Card className="bg-card border-border/50">
        <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Mid Price</span>
            <span className="text-3xl font-bold font-mono tabular-nums text-primary" data-testid="current-price">
              {ticker?.price ? parseFloat(ticker.price).toFixed(8) : "--"}
            </span>
          </div>
          <div className="flex items-center gap-6 text-xs">
            <div>
              <span className="text-muted-foreground">Best Bid: </span>
              <span className="font-mono text-emerald-500">{orderbook.bids?.[0]?.[0] || "--"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Best Ask: </span>
              <span className="font-mono text-red-400">{orderbook.asks?.[0]?.[0] || "--"}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Spread: </span>
              <span className="font-mono text-amber-400">
                {orderbook.bids?.[0] && orderbook.asks?.[0]
                  ? (parseFloat(orderbook.asks[0][0]) - parseFloat(orderbook.bids[0][0])).toFixed(8)
                  : "--"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Depth Summary - Real-time USDT depth at multiple % bands */}
      <Card className="bg-card border-border/50" data-testid="depth-summary-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            Orderbook Depth (USDT)
            <Badge variant="outline" className="ml-2 text-[10px] font-normal">Real-time</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(depthSummary?.bands || [{ pct: 2 }, { pct: 10 }, { pct: 20 }]).map((band) => {
              const bid = band.bid_usdt || 0;
              const ask = band.ask_usdt || 0;
              const total = bid + ask;
              const bidPct = total > 0 ? (bid / total) * 100 : 50;
              const askPct = total > 0 ? (ask / total) * 100 : 50;
              return (
                <div key={band.pct} className="rounded-lg border border-border/40 bg-secondary/30 p-3 space-y-2" data-testid={`depth-band-${band.pct}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Within ±{band.pct}%
                    </span>
                    <span className="text-sm font-mono font-bold text-primary tabular-nums" data-testid={`depth-total-${band.pct}`}>
                      {fmtUsdt(band.total_usdt)}
                    </span>
                  </div>

                  {/* Visual ratio bar */}
                  <div className="flex h-6 rounded-md overflow-hidden border border-border/30">
                    <div
                      className="bg-emerald-500/25 border-r border-emerald-500/40 flex items-center justify-end px-2"
                      style={{ width: `${bidPct}%`, minWidth: bid > 0 ? "10%" : "0%" }}
                      title={`Bids: ${fmtUsdt(bid)}`}
                    >
                      {bidPct > 15 && (
                        <span className="text-[10px] font-mono font-semibold text-emerald-400 truncate">
                          {fmtUsdt(bid)}
                        </span>
                      )}
                    </div>
                    <div
                      className="bg-red-500/25 flex items-center justify-start px-2"
                      style={{ width: `${askPct}%`, minWidth: ask > 0 ? "10%" : "0%" }}
                      title={`Asks: ${fmtUsdt(ask)}`}
                    >
                      {askPct > 15 && (
                        <span className="text-[10px] font-mono font-semibold text-red-400 truncate">
                          {fmtUsdt(ask)}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between text-[10px] font-mono">
                    <div className="text-emerald-500" data-testid={`depth-bid-${band.pct}`}>
                      <span className="text-muted-foreground">BUY ({band.bid_count || 0}): </span>
                      {fmtUsdt(bid)}
                    </div>
                    <div className="text-red-400" data-testid={`depth-ask-${band.pct}`}>
                      <span className="text-muted-foreground">SELL ({band.ask_count || 0}): </span>
                      {fmtUsdt(ask)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {depthSummary?.mid_price && (
            <p className="text-[10px] text-muted-foreground mt-3 font-mono">
              Mid: {parseFloat(depthSummary.mid_price).toFixed(8)} · Updates every 3s
            </p>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bids */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-emerald-500 flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />Bids (Buy)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-0.5">
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1 border-b border-border/30">
                <span>Price</span><span>Quantity</span>
              </div>
              {(orderbook.bids || []).slice(0, 15).map((bid, i) => (
                <div key={i} className="orderbook-row flex justify-between items-center py-0.5 px-1 rounded relative" data-testid={`bid-row-${i}`}>
                  <div className="absolute inset-0 bg-emerald-500/8 rounded" style={{ width: `${(parseFloat(bid[1]) / maxBidQty) * 100}%` }} />
                  <span className="font-mono text-xs text-emerald-500 relative z-10 tabular-nums">{parseFloat(bid[0]).toFixed(8)}</span>
                  <span className="font-mono text-xs text-muted-foreground relative z-10 tabular-nums">{parseFloat(bid[1]).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Asks */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-red-400 flex items-center gap-2">
              <ArrowUpDown className="w-4 h-4" />Asks (Sell)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-0.5">
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1 border-b border-border/30">
                <span>Price</span><span>Quantity</span>
              </div>
              {(orderbook.asks || []).slice(0, 15).map((ask, i) => (
                <div key={i} className="orderbook-row flex justify-between items-center py-0.5 px-1 rounded relative" data-testid={`ask-row-${i}`}>
                  <div className="absolute right-0 inset-y-0 bg-red-500/8 rounded" style={{ width: `${(parseFloat(ask[1]) / maxAskQty) * 100}%` }} />
                  <span className="font-mono text-xs text-red-400 relative z-10 tabular-nums">{parseFloat(ask[0]).toFixed(8)}</span>
                  <span className="font-mono text-xs text-muted-foreground relative z-10 tabular-nums">{parseFloat(ask[1]).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Trades */}
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <BookOpen className="w-4 h-4" />Recent Trades
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-0.5">
              <div className="flex justify-between text-[10px] uppercase tracking-wider text-muted-foreground font-semibold pb-1 border-b border-border/30">
                <span>Price</span><span>Qty</span><span>Time</span>
              </div>
              {trades.map((t, i) => (
                <div key={i} className="flex justify-between items-center py-0.5 px-1 text-xs" data-testid={`trade-row-${i}`}>
                  <span className={`font-mono tabular-nums ${t.isBuyerMaker ? "text-red-400" : "text-emerald-500"}`}>
                    {parseFloat(t.price).toFixed(8)}
                  </span>
                  <span className="font-mono tabular-nums text-muted-foreground">{parseFloat(t.qty).toLocaleString()}</span>
                  <span className="font-mono tabular-nums text-muted-foreground/60 text-[10px]">
                    {new Date(t.time).toLocaleTimeString()}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
